/** JSON value types and helpers shared by the protocol, the client and the server. */

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

/** Deep copy through JSON (drops undefined/functions, like the wire does). */
export function cloneJson<T>(v: T): T {
  return v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T);
}

/** UTF-8 byte length of a string without allocating a buffer. */
export function utf8Length(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const d = s.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) {
        n += 4;
        i++;
      } else n += 3;
    } else n += 3;
  }
  return n;
}

/** UTF-8 encoding without relying on TextEncoder (kept tiny and dependency free). */
export function utf8Encode(s: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s);
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    let c = s.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const d = s.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) {
        c = 0x10000 + ((c - 0xd800) << 10) + (d - 0xdc00);
        i++;
      }
    }
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return Uint8Array.from(out);
}

/**
 * A lone UTF-16 surrogate (half of an emoji). With the u flag a surrogate pair is one code point outside \p{Cs}, so only
 * an unpaired half matches. A regular expression, not String.prototype.isWellFormed: this module runs in older
 * browsers too.
 */
export const LONE_SURROGATE = /\p{Cs}/u;

/**
 * `s` with every lone UTF-16 surrogate replaced by U+FFFD (what a UTF-8 encoder writes for one): text that strict JSON
 * decoders of other platforms (Swift, Kotlin serialization, serde_json; I-JSON, RFC 7493) accept.
 */
export function wellFormedText(s: string): string {
  return LONE_SURROGATE.test(s) ? s.replace(/\p{Cs}/gu, '\ufffd') : s;
}

/**
 * Whether a string or a member name anywhere inside JSON data `v` holds a lone UTF-16 surrogate. Iterative, so that no
 * nesting depth overflows the stack (for data whose depth nothing else bounds).
 */
export function hasLoneSurrogate(v: unknown): boolean {
  const stack: unknown[] = [v];
  while (stack.length > 0) {
    const x = stack.pop();
    if (typeof x === 'string') {
      if (LONE_SURROGATE.test(x)) return true;
    } else if (Array.isArray(x)) {
      for (const e of x) stack.push(e);
    } else if (typeof x === 'object' && x !== null) {
      for (const [k, e] of Object.entries(x)) {
        if (LONE_SURROGATE.test(k)) return true;
        stack.push(e);
      }
    }
  }
  return false;
}

/**
 * Checks that a value is plain JSON data (no cycles, functions, class instances, non-finite numbers, no "__proto__"
 * member, no lone UTF-16 surrogate in a string or a member name), bounded in depth. Returns an error path or null. The
 * path names only member names already checked, so it never carries a lone surrogate itself.
 */
export function checkJsonData(v: unknown, maxDepth = 64, path = '$'): string | null {
  const seen = new Set<object>();
  const walk = (x: unknown, depth: number, p: string): string | null => {
    if (x === null) return null;
    switch (typeof x) {
      case 'string':
        return LONE_SURROGATE.test(x) ? `${p}: lone UTF-16 surrogate (not well-formed text)` : null;
      case 'boolean':
        return null;
      case 'number':
        return Number.isFinite(x) ? null : `${p}: non-finite number`;
      case 'object':
        break;
      default:
        return `${p}: unsupported ${typeof x}`;
    }
    if (depth > maxDepth) return `${p}: nesting deeper than ${maxDepth}`;
    const o = x as object;
    if (seen.has(o)) return `${p}: cyclic or shared reference`;
    seen.add(o);
    if (Array.isArray(o)) {
      for (let i = 0; i < o.length; i++) {
        const e = walk(o[i], depth + 1, `${p}[${i}]`);
        if (e) return e;
      }
    } else {
      if (!isPlainObject(o)) return `${p}: not a plain object`;
      for (const [k, val] of Object.entries(o)) {
        if (val === undefined) continue;
        // An own "__proto__" member (JSON.parse creates one) sets the prototype of any object it is assigned into.
        if (k === '__proto__') return `${p}.__proto__: reserved key`;
        if (LONE_SURROGATE.test(k)) return `${p}: a member name with a lone UTF-16 surrogate (not well-formed text)`;
        const e = walk(val, depth + 1, `${p}.${k}`);
        if (e) return e;
      }
    }
    seen.delete(o);
    return null;
  };
  return walk(v, 0, path);
}
